/**
 * ImportExportModal.jsx - ImportExportModal - Import/Export lists
 * 
 * CHANGE HISTORY:
 * - 251018_1327 [43ebfec] danielbeer: refactor: restructure packages - extract all nested packages to top level
 */
import React, { useState } from 'react';
import { useOrganizr } from '../src/OrganizrContext';

const ImportExportModal = ({ selectedListId, onClose }) => {
  const { exportList, importList, createList } = useOrganizr();
  const [mode, setMode] = useState('export'); // export or import
  const [format, setFormat] = useState('json');
  const [importData, setImportData] = useState('');
  const [exportData, setExportData] = useState('');
  
  const handleExport = () => {
    if (!selectedListId) {
      alert('Please select a list to export');
      return;
    }
    
    const data = exportList(selectedListId, format);
    
    if (format === 'json') {
      setExportData(data);
    } else {
      // Convert to requested format using Parsely
      const parser = new Parsely();
      const exported = parser.export(data, format);
      setExportData(exported);
    }
  };
  
  const handleImport = () => {
    if (!importData.trim()) {
      alert('Please paste data to import');
      return;
    }
    
    try {
      const parser = new Parsely();
      const result = parser.parse(importData);
      
      if (result.error) {
        alert(`Parse error: ${result.error}`);
        return;
      }
      
      // Create new list from imported data
      const items = result.data.map((item, index) => ({
        name: typeof item === 'string' ? item : (item.name || item.text || JSON.stringify(item)),
        value: typeof item === 'string' ? item.toLowerCase().replace(/\s+/g, '-') : item.value,
        order: index
      }));
      
      const newList = createList({
        name: `Imported List (${result.format})`,
        children: items
      });
      
      alert(`Successfully imported ${items.length} items!`);
      onClose();
    } catch (error) {
      alert(`Import error: ${error.message}`);
    }
  };
  
  const handleCopy = () => {
    navigator.clipboard.writeText(exportData);
    alert('Copied to clipboard!');
  };
  
  const handleDownload = () => {
    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `organizr-export.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="organizr-modal-overlay" onClick={onClose}>
      <div className="organizr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="organizr-modal-header">
          <h3>Import / Export</h3>
          <button onClick={onClose} className="btn-modal-close">×</button>
        </div>
        
        <div className="organizr-modal-body">
          <div className="import-export-tabs">
            <button
              className={`tab ${mode === 'export' ? 'active' : ''}`}
              onClick={() => setMode('export')}
            >
              Export
            </button>
            <button
              className={`tab ${mode === 'import' ? 'active' : ''}`}
              onClick={() => setMode('import')}
            >
              Import
            </button>
          </div>
          
          <div className="import-export-format">
            <label>Format:</label>
            <select value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
              <option value="tsv">TSV</option>
              <option value="markdown">Markdown</option>
              <option value="list">Plain List</option>
            </select>
          </div>
          
          {mode === 'export' ? (
            <div className="export-section">
              <button onClick={handleExport} className="btn-export">
                Generate Export
              </button>
              
              {exportData && (
                <>
                  <textarea
                    value={exportData}
                    readOnly
                    className="export-textarea"
                    rows={12}
                  />
                  
                  <div className="export-actions">
                    <button onClick={handleCopy} className="btn-copy">
                      Copy to Clipboard
                    </button>
                    <button onClick={handleDownload} className="btn-download">
                      Download File
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="import-section">
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="Paste your data here..."
                className="import-textarea"
                rows={12}
              />
              
              <button onClick={handleImport} className="btn-import">
                Import Data
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportExportModal;
